import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { allTravelDnaSteps } from '@/_constants/TravelDnaStep.ts';
import TravelDnaStep from '@/_constants/TravelDnaStep.ts';
import { CONSENT_POLICY_VERSION } from '$/_const/dna/ConsentPolicyVersion.ts';
import type { TravelDnaFormData } from '$/models/Dna.d.ts';
import type { OnCompleteArgs } from '@/components/functional/TravelDnaManipulationForm/TravelDnaManipulationForm.tsx';
import DnaStepLayout from '@/components/functional/TravelDnaManipulationForm/shared/DnaStepLayout.tsx';
import DnaPrimaryButton from '@/components/functional/TravelDnaManipulationForm/shared/DnaPrimaryButton.tsx';
import { ConsentComponent } from '@/components/functional/Consent/Consent.tsx';
import DnaError from '@/components/functional/TravelDnaManipulationForm/shared/DnaError.tsx';

const anythingElseSchema = z.object({
  anything_else_journi_should_know: z.string().trim().max(3000).nullable(),
  userConsent: z.boolean(),
}).refine((data) => !data.anything_else_journi_should_know || data.userConsent, {
  path: ['userConsent'],
  message: 'Please confirm that Journi may use this information when planning your trips.',
});

type AnythingElseForm = z.infer<typeof anythingElseSchema>;
export type AnythingElseSchema = Omit<AnythingElseForm, 'userConsent'>;

type AnythingElseProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

function AnythingElse({ onComplete, onCompleteWhenDirty }: AnythingElseProps) {
  const { t } = useTranslation();
  const { setContextData, contextData } = useStepperContext<
    typeof allTravelDnaSteps,
    TravelDnaFormData
  >();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const critical = contextData?.critical_requirements;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty, errors },
  } = useForm<AnythingElseForm>({
    resolver: zodResolver(anythingElseSchema),
    defaultValues: {
      userConsent: false,
      anything_else_journi_should_know:
        critical?.anything_else_journi_should_know ?? '',
    },
  });

  const containsInformation = Boolean(watch('anything_else_journi_should_know')?.trim());

  const onSubmit = (formData: AnythingElseForm) => {
    void (async () => {
      try {
        setIsPending(true);
        setError(null);
        const data: AnythingElseSchema = {
          anything_else_journi_should_know:
            formData.anything_else_journi_should_know?.trim() || null,
        };
        const context: TravelDnaFormData = {
          ...contextData,
          critical_requirements: {
            ...contextData?.critical_requirements,
            ...data,
          },
        };
        const args: OnCompleteArgs = {
          step: TravelDnaStep.ANYTHING_ELSE,
          consent: {
            page: TravelDnaStep.ANYTHING_ELSE,
            version: CONSENT_POLICY_VERSION,
            status: formData.userConsent ? 'granted' : 'withdrawn',
          },
          data,
          context,
        };

        if (isDirty || !critical) {
          setContextData(() => context);
          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError))
        );
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <DnaStepLayout
      eyebrow={t('travelDnaPage.anythingElse.header')}
      title={t('travelDnaPage.anythingElse.subheader')}
      description={t('travelDnaPage.anythingElse.description')}
      maxWidthClassName="max-w-[970px]"
    >
      <form
        className="mt-7 flex w-full flex-col items-center"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <DnaError error={error} className="mb-6" />
        <textarea
          {...register('anything_else_journi_should_know')}
          rows={7}
          className="w-full resize-y rounded-[10px] border border-[var(--gold-deep)] bg-[var(--gold-deep)] px-5 py-4 font-sans text-lg font-semibold text-white outline-none placeholder:text-white/75 focus:border-[var(--gold-lt)]"
          placeholder={t('travelDnaPage.anythingElse.placeholder')}
        />

        <div className="mt-5 w-full">
          <ConsentComponent
            userConsent={watch('userConsent')}
            setConsent={(checked) => setValue('userConsent', checked, { shouldDirty: true, shouldValidate: true })}
            invalid={containsInformation && Boolean(errors.userConsent)}
            disabled={isPending}
          />
        </div>

        <DnaPrimaryButton className="mt-16" loading={isPending} type="submit">
          {t('travelDnaPage.anythingElse.complete')}
        </DnaPrimaryButton>
      </form>
    </DnaStepLayout>
  );
}

export default AnythingElse;
